import {
  button_colors_styles_default
} from "./chunk.YAGRC5Z4.js";
import {
  __decorateClass,
  component_styles_default,
  theme_styles_default
} from "./chunk.U4VUMZVF.js";

// src/components/icon-button/icon-button.ts
import { LitElement, html } from "lit";
import { customElement, property, query } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";
import { ifDefined } from "lit/directives/if-defined.js";

// src/components/icon-button/icon-button.styles.ts
import { css } from "lit";
var icon_button_styles_default = css`
	${theme_styles_default}
	${component_styles_default}
	${button_colors_styles_default}

	:host {
		display: inline-block;
		width: auto;
	}

	:host([size='tiny']) {
		height: var(--yotpo-button-height-tiny);
		width: var(--yotpo-button-height-tiny);
	}

	:host([size='small']) {
		height: var(--yotpo-button-height-small);
		width: var(--yotpo-button-height-small);
	}

	:host([size='medium']) {
		height: var(--yotpo-button-height-medium);
		width: var(--yotpo-button-height-medium);
	}

	:host([size='large']) {
		height: var(--yotpo-button-height-large);
		width: var(--yotpo-button-height-large);
	}

	.icon-button {
		display: inline-flex;
		justify-content: center;
		align-items: center;
		box-sizing: border-box;
		border: var(--yotpo-button-border);
		border-radius: var(--yotpo-button-border-radius);
		vertical-align: middle;
		cursor: pointer;
		transition: var(--yotpo-transition-medium) border-color, var(--yotpo-transition-medium) background-color,
			var(--yotpo-transition-medium) color, var(--yotpo-transition-medium) box-shadow;
	}

	.icon-button::-moz-focus-inner {
		border: 0;
	}

	.icon-button:focus {
		outline: none;
	}

	.button--disabled {
		cursor: not-allowed;
	}

	/* When disabled, prevents mouse events from bubbling up */
	.button--disabled * {
		pointer-events: none;
	}

	/* Clicks on icons shouldn't prevent the button from gaining focus */
	.icon-button::slotted(yotpo-icon) {
		pointer-events: none;
	}

	/*
* Size 
*/

	.button--tiny {
		height: var(--yotpo-button-height-tiny);
		width: var(--yotpo-button-height-tiny);
		padding: var(--yotpo-button-vertical-spacing-tiny) var(--yotpo-button-vertical-spacing-tiny);
	}

	.button--small {
		height: var(--yotpo-button-height-small);
		width: var(--yotpo-button-height-small);
		padding: var(--yotpo-button-vertical-spacing-small) var(--yotpo-button-vertical-spacing-small);
	}

	.button--medium {
		height: var(--yotpo-button-height-medium);
		width: var(--yotpo-button-height-medium);
		padding: var(--yotpo-button-vertical-spacing-medium) var(--yotpo-button-vertical-spacing-medium);
	}

	.button--large {
		height: var(--yotpo-button-height-large);
		width: var(--yotpo-button-height-large);
		padding: var(--yotpo-button-vertical-spacing-large) var(--yotpo-button-vertical-spacing-large);
	}

	.icon-button yotpo-icon {
		align-items: center;
		font-size: var(--yotpo-icon-size);
		flex-shrink: 0;
	}
`;

// src/components/icon-button/icon-button.ts
var YotpoIconButton = class extends LitElement {
  constructor() {
    super(...arguments);
    this.name = "";
    this.priority = "primary" /* primary */;
    this.size = "medium" /* medium */;
    this.color = "brand" /* brand */;
    this.disabled = false;
    this.tooltipText = "";
    this.tooltipPlacement = "top" /* top */;
    this.library = "YotpoIconsLibrary";
  }
  click() {
    this.button.click();
  }
  handleClick(event) {
    if (this.disabled) {
      event.preventDefault();
      event.stopPropagation();
    }
  }
  render() {
    const icon = html` <yotpo-icon name=${ifDefined(this.name)} aria-hidden="true"></yotpo-icon> `;
    const button = html`
			<button
				part="base"
				class=${classMap({
      "icon-button": true,
      "button--disabled": this.disabled,
      "button--primary": this.priority === "primary" /* primary */,
      "button--secondary": this.priority === "secondary" /* secondary */,
      "button--tertiary": this.priority === "tertiary" /* tertiary */,
      "button--tiny": this.size === "tiny" /* tiny */,
      "button--small": this.size === "small" /* small */,
      "button--medium": this.size === "medium" /* medium */,
      "button--large": this.size === "large" /* large */,
      "button--brand": this.color === "brand" /* brand */,
      "button--destructive": this.color === "destructive" /* destructive */,
      "button--premium": this.color === "premium" /* premium */,
      "button--dark": this.color === "dark" /* dark */
    })}
				?disabled=${this.disabled}
				type="button"
				aria-label=${this.tooltipText}
				tabindex=${this.disabled ? "-1" : "0"}
				@click=${(event) => this.handleClick(event)}
			>
				${icon}
			</button>
		`;
    return this.tooltipText ? html`
					<yotpo-tooltip
						text=${this.tooltipText}
						tooltipPlacement=${ifDefined(this.tooltipPlacement)}
						hoist=${true}
					>
						${button}
					</yotpo-tooltip>
			  ` : button;
  }
};
YotpoIconButton.styles = icon_button_styles_default;
__decorateClass([
  query("button")
], YotpoIconButton.prototype, "button", 2);
__decorateClass([
  property({ reflect: true })
], YotpoIconButton.prototype, "name", 2);
__decorateClass([
  property({ reflect: true })
], YotpoIconButton.prototype, "priority", 2);
__decorateClass([
  property({ reflect: true })
], YotpoIconButton.prototype, "size", 2);
__decorateClass([
  property({ reflect: true })
], YotpoIconButton.prototype, "color", 2);
__decorateClass([
  property({ type: Boolean, reflect: true })
], YotpoIconButton.prototype, "disabled", 2);
__decorateClass([
  property({ reflect: true })
], YotpoIconButton.prototype, "tooltipText", 2);
__decorateClass([
  property({ reflect: true })
], YotpoIconButton.prototype, "tooltipPlacement", 2);
YotpoIconButton = __decorateClass([
  customElement("yotpo-icon-button")
], YotpoIconButton);

export {
  YotpoIconButton
};
